import React, { useEffect } from "react";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SocketProvider } from "./contexts/SocketContext";
import { ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import AuthPage from "./pages/AuthPage";
import ChatPage from "./pages/ChatPage";

const logBootTiming = (event) => {
  try {
    if (localStorage.getItem('whisper_debug_boot_timing') !== 'true') return;
    console.info('[whisper boot]', event, { t: Math.round(performance.now()) });
  } catch {
    // Debug logging should never affect startup.
  }
};

// Lightweight boot shell shown while auth is being checked.
const BootShell = () => (
  <div className="fixed inset-0 bg-surface-0 flex overflow-hidden">
    <div className="hidden md:flex md:flex-col w-[340px] border-r border-border-subtle bg-surface-1 p-4">
      <div className="flex items-center gap-3 mb-7 px-1">
        <div className="w-9 h-9 rounded-xl bg-brand-gradient-soft ui-skeleton" />
        <div className="flex-1">
          <div className="w-24 h-3.5 ui-skeleton" />
          <div className="w-16 h-2.5 ui-skeleton mt-2" />
        </div>
      </div>
      <div className="h-10 rounded-xl ui-skeleton mb-4" />
      <div className="space-y-1.5">
        {[0, 1, 2, 3, 4, 5].map((item) => (
          <div key={item} className="flex items-center gap-3 rounded-xl px-2 py-2.5">
            <div className="w-11 h-11 rounded-full ui-skeleton shrink-0" />
            <div className="flex-1">
              <div className="w-2/3 h-3 ui-skeleton" />
              <div className="w-1/2 h-2.5 ui-skeleton mt-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="flex-1 flex flex-col bg-surface-0">
      <div className="h-16 border-b border-border-subtle bg-surface-1 flex items-center gap-3 px-4">
        <div className="w-10 h-10 rounded-full ui-skeleton" />
        <div>
          <div className="w-36 h-3.5 ui-skeleton" />
          <div className="w-20 h-2.5 ui-skeleton mt-2" />
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-brand-gradient flex items-center justify-center mx-auto mb-5 shadow-elev-lg">
            <div className="w-6 h-6 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />
          </div>
          <h1 className="text-white text-2xl font-semibold font-heading tracking-tight">Whisper</h1>
          <p className="text-zinc-500 mt-2 text-sm">Preparing your conversations…</p>
        </div>
      </div>
    </div>
  </div>
);

// Main app content with auth check
const AppContent = () => {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading) logBootTiming(user ? 'first-interactive-render' : 'auth-page-render');
  }, [loading, user]);

  if (loading) {
    return <BootShell />;
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <SocketProvider>
      <ChatPage />
    </SocketProvider>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
